import { FollowerTemplateSchema, type FollowerTemplate } from '../../schemas/follower';

/**
 * Template del **Braccio Destro del Warlock** (manuale V1, rr. 5997-6020).
 *
 * 6° template Follower del sistema unificato FI-8. Primo template
 * `type: 'hireling'` con **Lealtà** (gregario classico) e Costo
 * narrativo.
 *
 * **Pattern stress-testati a beneficio dello schema D8b**:
 *  - **`type: 'hireling'`** (prima istanza)
 *  - **`primarySkillFromNarrativeField`** (D8b): Abilità primaria scelta
 *    dal giocatore via Select oneOf 9. Inizia a Rango 1 (manuale r. 6001:
 *    «scegli per il servitore un'Abilità primaria a Rango 1»).
 *  - **`traitFromNarrativeFields`** (D8b): 1 capacità utile sempre presente,
 *    label/desc compilate dal giocatore al reclutamento (manuale r. 6001:
 *    «una capacità utile da concordare con il GM»).
 *  - **`modifierBonus` su 6 stat** (libero, no subset come Compagno Animale):
 *    +2 a una Caratteristica scelta dal giocatore (manuale r. 5999).
 *  - **`hpRule: scaling`** (1 + 2×lvl, max 21) — uguale al Compagno Animale.
 *  - **`respawnRule` "tramonto"** (manuale r. 6011): pattern unico nel
 *    manuale — niente rituale, niente nuovo legame.
 *
 * **Stats fisse**:
 *  - Livello: 2 (chain advanced Servitore Competente / Maestria del Servitore
 *    rinviata a FI-41 — "upgrade with choice")
 *  - Lealtà: 2 (NON 1 default come per *Reclutare*)
 *  - PF: 1 + 2×livello-PG (max 21)
 *  - Mod: tutti +1, +2 a 1 stat scelta
 *  - Carico: 6
 *  - Dado di Danno: d6
 *  - Costo: «Portare avanti i piani del patrono» (manuale r. 5999)
 *
 * **Note di design**:
 *  - Niente `recruitRoll`: il Braccio Destro non si attiva con un tiro al
 *    reclutamento — è una mossa "ottenuta al level-up" deterministica.
 *  - Niente `archetypeTagBundle` né `archetypeFromChoiceGroup`: il Braccio
 *    Destro non ha varianti di archetype (è un PNG con caratteristiche
 *    narrativamente libere — descrivibile via `descrizione-emissario`).
 *  - Niente `peculiaritiesFromChoiceGroup`: le scelte del giocatore
 *    (Abilità + capacità) sono modellate direttamente come narrativeField
 *    + `primarySkillFromNarrativeField` + `traitFromNarrativeFields`.
 */
export const warlockBraccioDestroTemplate: FollowerTemplate = FollowerTemplateSchema.parse({
  id: 'warlock-braccio-destro',
  sourceMoveId: 'warlock.braccio-destro',
  type: 'hireling',
  label: 'Braccio Destro',
  baseStats: {
    level: 2,
    modifiers: {
      forza: 1,
      destrezza: 1,
      costituzione: 1,
      intelligenza: 1,
      saggezza: 1,
      carisma: 1,
    },
    hpRule: { type: 'scaling', base: 1, perCharacterLevel: 2, max: 21 },
    damageDie: 'd6',
    load: 6,
    tags: [],
  },
  fixedSkills: [],
  fixedTraits: [],
  defaultLoyalty: 2,
  costMotivation: 'Portare avanti i piani del patrono',
  modifierBonus: { fromChoiceGroup: 'modificatore', amount: 2 },
  primarySkillFromNarrativeField: {
    narrativeFieldId: 'abilita-primaria',
    rank: 1,
  },
  traitFromNarrativeFields: {
    traitId: 'braccio-destro-capacita',
    labelFieldId: 'capacita-utile-label',
    descriptionFieldId: 'capacita-utile-desc',
  },
  nameFromNarrativeField: 'nome-emissario',
  respawnRule:
    'Se il Braccio Destro muore o per qualunque motivo non è più disponibile, torna al tuo fianco al prossimo tramonto, vivo e pronto per nuove avventure.',
  // D8c — FI-41: chain advanced (Servitore Competente L2-5 + Maestria del
  // Servitore L6-10). Le mosse advanced del PG (`warlock.servitore-competente`,
  // `warlock.maestria-del-servitore`) dichiarano i propri `choiceGroups` e
  // `narrativeFields` (vedi `data/moves/warlock/advanced-*.ts`). Il template
  // mappa le opzioni a effetti del Follower:
  //  - primary skill rank override (statico: R1→R2 con Servitore Competente,
  //    R2→R3 con Maestria)
  //  - level/mod statici (solo Maestria)
  //  - choice-based effects (skill-rank-from-narrative + custom-trait-from-narrative)
  upgrades: [
    {
      fromMoveId: 'warlock.servitore-competente',
      primarySkillRankOverride: 2,
      peculiaritiesFromChoiceGroup: {
        fromChoiceGroup: 'bonus',
        optionToEffect: {
          'due-sec-r1': [
            {
              kind: 'skill-rank-from-narrative',
              narrativeFieldId: 'sc-quale-sec1',
              baseRank: 1,
              isPrimary: false,
            },
            {
              kind: 'skill-rank-from-narrative',
              narrativeFieldId: 'sc-quale-sec2',
              baseRank: 1,
              isPrimary: false,
            },
          ],
          'sec-r1-piu-cap': [
            {
              kind: 'skill-rank-from-narrative',
              narrativeFieldId: 'sc-quale-sec',
              baseRank: 1,
              isPrimary: false,
            },
            {
              kind: 'custom-trait-from-narrative',
              labelFieldId: 'sc-cap-label',
              descriptionFieldId: 'sc-cap-desc',
            },
          ],
        },
      },
    },
    {
      fromMoveId: 'warlock.maestria-del-servitore',
      levelOverride: 3,
      modifierBonusOverride: { fromChoiceGroup: 'modificatore', amount: 3 },
      primarySkillRankOverride: 3,
      peculiaritiesFromChoiceGroup: {
        fromChoiceGroup: 'bonus',
        optionToEffect: {
          'nuova-capacita': [
            {
              kind: 'custom-trait-from-narrative',
              labelFieldId: 'maestria-cap-label',
              descriptionFieldId: 'maestria-cap-desc',
            },
          ],
          'sec-r2': [
            {
              kind: 'skill-rank-from-narrative',
              narrativeFieldId: 'maestria-sec-quale',
              baseRank: 2,
              isPrimary: false,
            },
          ],
        },
      },
    },
  ],
});
